import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Sparkles, Plus, FileText, ScrollText,
  ClipboardPaste, Check, Trash2, ArrowUpRight, Calculator,
} from "lucide-react";
import { formatDate, brDatesInText } from "@/lib/format";
import { maskDateInput, maskTimeInput, dateBrToIso, isoToDateBr, isValidTime } from "@/lib/dateTimeMask";
import PrazoCalculator from "@/components/PrazoCalculator";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import LastUpdatedLabel from "@/components/LastUpdatedLabel";

import { Dica } from "@/components/Dica";
import ProcessoLink from "@/components/ProcessoLink";
// Mesma tabela do backend (server.py: PARTY_LABELS) — o rótulo do campo muda conforme a
// área do processo (trabalhista usa Reclamante/Reclamada, as demais usam Autor/Réu).
const CASE_AREAS = [
  { value: "trabalhista", label: "Trabalhista" },
  { value: "civel", label: "Cível" },
  { value: "penal", label: "Penal" },
  { value: "outro", label: "Outro" },
];
const PARTY_LABELS = {
  trabalhista: ["Reclamante", "Reclamada"],
  civel: ["Autor", "Réu"],
  penal: ["Autor", "Réu"],
  outro: ["Autor", "Réu"],
};

// Compatibilidade: publicações antigas só têm a string livre "Fulano x Sicrano" — tenta
// separar em autor/réu pra preencher os campos novos (best-effort), sem apagar nada.
function withPartySplit(hearing) {
  const h = { ...hearing, case_area: hearing.case_area || "outro" };
  if (!h.party_author && !h.party_defendant && h.parties && h.parties.includes(" x ")) {
    const [a, d] = h.parties.split(" x ");
    h.party_author = a?.trim() || "";
    h.party_defendant = d?.trim() || "";
  }
  return h;
}

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  // Lote 132: no celular a lista fica em cima e o detalhe embaixo; ao marcar um
  // item, rola até o detalhe (só quando troca de publicação, não a cada edição).
  const detalheRef = useRef(null);
  const selId = selected?.pub_id;
  useEffect(() => {
    if (!selId || typeof window === "undefined" || window.innerWidth >= 1024) return;
    detalheRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [selId]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  // Lote 29: data/hora editados como texto com máscara ("dd/mm/aaaa" / "hh:mm"),
  // separado de selected.hearing.date/time (que ficam em ISO, formato do backend) —
  // ver frontend/src/lib/dateTimeMask.js pro motivo da troca do campo nativo.
  const [dateText, setDateText] = useState("");
  const [timeText, setTimeText] = useState("");

  useEffect(() => {
    setDateText(isoToDateBr(selected?.hearing?.date));
    setTimeText(selected?.hearing?.time || "");
  }, [selected?.pub_id]);

  // Lote 41 (fusão da aba "Prazos" em "Para validar"): itens com source
  // "auto_prazo_audiencia" são o lembrete "conferir prazos da ata" — em vez do formulário
  // padrão de editar+validar, mostram o fluxo colar-ata → IA sugere → revisar → confirmar
  // que antes vivia em Prazos.jsx. Estado próprio, reiniciado a cada troca de item.
  const [ataOpen, setAtaOpen] = useState(false);
  const [ataText, setAtaText] = useState("");
  const [ataSuggestions, setAtaSuggestions] = useState(null);
  const [ataBusy, setAtaBusy] = useState(false);

  useEffect(() => {
    setAtaOpen(false);
    setAtaText("");
    setAtaSuggestions(null);
  }, [selected?.pub_id]);

  const isAtaItem = selected?.source === "auto_prazo_audiencia";
  // Lote 83: a data do lembrete (audiência + dias úteis) ainda não chegou? Comparação de ISO local.
  const hojeIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const ataAindaNaoChegou = isAtaItem && !!selected?.hearing?.date && selected.hearing.date > hojeIso;
  // Lote 57 (pedido do Robson): quando a audiência original desse lembrete foi agendada à
  // mão (Novo evento), em vez do fluxo normal de "veio do DJEN/DataJud", o card muda —
  // ver origin_source calculado em ensure_prazo_pendencia (backend).
  const isManualAudiencia = isAtaItem && selected?.hearing?.origin_source === "manual";

  // Lote 42: Calculadora de prazos — modal único reaproveitado em dois pontos (botão no
  // cabeçalho, em branco, e a partir do card "Conferir ata", pré-preenchido com a data da
  // audiência de origem). `calcPrefill` guarda o pré-preenchimento da vez, ou null pro
  // cálculo avulso.
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcPrefill, setCalcPrefill] = useState(null);
  const openCalculator = (prefill = null) => {
    setCalcPrefill(prefill);
    setCalcOpen(true);
  };

  const ataSugerir = async () => {
    if (!ataText.trim()) {
      toast.error("Cole o texto da ata antes de continuar");
      return;
    }
    setAtaBusy(true);
    try {
      const r = await http.post(`/prazos/${selected.pendencia_id}/extrair`, { text: ataText });
      const sugeridos = (r.data.suggestions || []).map((s) => ({ ...s, date: isoToDateBr(s.date), notes: "" }));
      const skipped = r.data.skipped?.length || 0;
      if (sugeridos.length === 0) {
        toast.message("Nenhum prazo com data identificado na ata", {
          description: skipped > 0 ? `${skipped} mencionado(s) sem data calculável — adicione manualmente se precisar.` : undefined,
        });
      }
      setAtaSuggestions(sugeridos);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao analisar a ata");
    } finally { setAtaBusy(false); }
  };

  const updateAtaSuggestion = (idx, field, value) => {
    setAtaSuggestions((cur) => { const list = [...(cur || [])]; list[idx] = { ...list[idx], [field]: value }; return list; });
  };
  const removeAtaSuggestion = (idx) => {
    setAtaSuggestions((cur) => (cur || []).filter((_, i) => i !== idx));
  };

  const ataConfirmar = async () => {
    setAtaBusy(true);
    try {
      const r = await http.post(`/prazos/${selected.pendencia_id}/confirmar`, {
        items: (ataSuggestions || []).map(({ description, date, time, notes, deadline_type }) => ({
          description,
          date: dateBrToIso(date),
          time: isValidTime(time) ? time : "",
          notes,
          deadline_type: deadline_type || null,
        })),
      });
      const n = r.data.created?.length || 0;
      toast.success(n > 0 ? `${n} prazo${n === 1 ? "" : "s"} agendado${n === 1 ? "" : "s"}` : "Pendência resolvida sem agendar prazos");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar os prazos");
    } finally { setAtaBusy(false); }
  };

  const ataDispensar = async () => {
    setAtaBusy(true);
    try {
      await http.post(`/prazos/${selected.pendencia_id}/dispensar`);
      toast.success("Marcado como nada a fazer");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao dispensar");
    } finally { setAtaBusy(false); }
  };

  const load = async () => {
    const r = await http.get("/publications", { params: { filter: "to_validate" } });
    setItems(r.data);
    const withSplit = (p) => p && { ...p, hearing: withPartySplit(p.hearing) };
    if (r.data.length > 0 && !selected) setSelected(withSplit(r.data[0]));
    if (selected && !r.data.find((i) => i.pub_id === selected.pub_id)) setSelected(withSplit(r.data[0]) || null);
  };
  const loadClients = async () => {
    const r = await http.get("/clients");
    setClients(r.data);
  };

  useEffect(() => { load(); loadClients(); }, []);
  // Lote 57: atualiza sozinho ao voltar pra tela e a cada 15min enquanto fica aberta.
  const { lastUpdated } = useAutoRefresh(load);

  const saveEdit = async () => {
    if (!selected) return;
    const date = dateBrToIso(dateText);
    const time = isValidTime(timeText) ? timeText : "";
    setLoading(true);
    try {
      const r = await http.put(`/publications/${selected.pub_id}`, {
        date,
        time,
        court: selected.hearing.court,
        court_district: selected.hearing.court_district,
        case_number: selected.hearing.case_number,
        client_id: selected.client_id,
        case_area: selected.hearing.case_area,
        party_author: selected.hearing.party_author,
        party_defendant: selected.hearing.party_defendant,
        deadline_type: selected.hearing.deadline_type,
      });
      setSelected({ ...r.data, hearing: withPartySplit(r.data.hearing) });
      toast.success("Dados atualizados");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Erro ao salvar", {
        description: detail ? undefined : `HTTP ${e?.response?.status ?? "sem resposta"} — ${e?.message || "erro desconhecido"}`,
        duration: 12000,
      });
    } finally { setLoading(false); }
  };

  const approve = async () => {
    if (!selected) return;
    const date = dateBrToIso(dateText);
    const time = isValidTime(timeText) ? timeText : "";
    const missing = [];
    if (!date) missing.push(dateText ? "data (use o formato dd/mm/aaaa)" : "data");
    if (!time) missing.push(timeText ? "hora (use o formato hh:mm)" : "hora");
    if (missing.length > 0) {
      toast.error(`Preencha: ${missing.join(", ")}`, { duration: 6000 });
      return;
    }
    setLoading(true);
    try {
      await http.put(`/publications/${selected.pub_id}`, {
        date, time,
        court: selected.hearing.court,
        court_district: selected.hearing.court_district,
        case_number: selected.hearing.case_number,
        client_id: selected.client_id,
        case_area: selected.hearing.case_area,
        party_author: selected.hearing.party_author,
        party_defendant: selected.hearing.party_defendant,
        deadline_type: selected.hearing.deadline_type,
      });
      const approveResp = await http.post(`/publications/${selected.pub_id}/approve`);
      toast.success("Dados validados", { description: "Já sincronizado com o Google Calendar." });
      // Lote 34.2b: alerta não-bloqueante de conflito de horário — não impede a validação.
      const conflicts = approveResp.data?.conflicts || [];
      if (conflicts.length > 0) {
        const other = conflicts[0];
        toast.warning("Conflito de horário", {
          description: `Esse horário está perto de ${other.label.toLowerCase()} às ${other.time}${
            conflicts.length > 1 ? ` (e mais ${conflicts.length - 1})` : ""
          } — confira a agenda.`,
          duration: 10000,
          action: {
            label: "Ver conflito",
            onClick: () => navigate(`/calendario?data=${approveResp.data?.hearing?.date || ""}&conflito=${[approveResp.data?.pub_id, ...conflicts.map((c) => c.pub_id)].filter(Boolean).join(",")}`),
          },
        });
      }
      setSelected(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Erro ao validar", {
        description: detail ? undefined : `HTTP ${e?.response?.status ?? "sem resposta"} — ${e?.message || "erro desconhecido"}`,
        duration: 12000,
      });
    } finally { setLoading(false); }
  };

  const reject = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await http.post(`/publications/${selected.pub_id}/reject`);
      toast.info("Publicação descartada");
      setSelected(null);
      load();
    } catch { toast.error("Erro ao rejeitar"); }
    finally { setLoading(false); }
  };

  const updateHearing = (k, v) => setSelected({ ...selected, hearing: { ...selected.hearing, [k]: v } });

  return (
    <div className="lg:h-screen flex flex-col">
      <header className="px-4 sm:px-8 py-4 sm:py-6 border-b border-stone-200 bg-white flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-1">
            Para validar
          </div>
          <h1 className="font-display text-lg sm:text-2xl font-semibold text-stone-900">
            {items.length} publicação(ões) agendadas — aguardando sua conferência
          </h1>
          <p className="text-xs text-stone-500 mt-1">
            Já estão no Google Calendar. Validar é opcional — sirva-se para conferir os dados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="open-prazo-calculator-button"
            variant="outline"
            className="gap-2"
            onClick={() => openCalculator(null)}
          >
            <Calculator className="w-4 h-4" strokeWidth={1.75} /> Calcular prazo
          </Button>
          <ManualPubDialog onCreated={load} />
          <LastUpdatedLabel date={lastUpdated} />
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-rows-1 lg:grid-cols-3 lg:overflow-hidden">
        <aside className="lg:col-span-1 border-b lg:border-b-0 lg:border-r border-stone-200 bg-white overflow-y-auto max-h-[28dvh] lg:max-h-none" data-testid="queue-list">
          {items.length === 0 && (
            <div className="p-8 text-center text-sm text-stone-500">
              <ScrollText className="w-8 h-8 mx-auto mb-2 text-stone-300" strokeWidth={1.25} />
              Nada aguardando revisão.
            </div>
          )}
          {items.map((p) => (
            <button
              key={p.pub_id}
              data-testid={`queue-item-${p.pub_id}`}
              onClick={() => setSelected({ ...p, hearing: withPartySplit(p.hearing) })}
              aria-current={selected?.pub_id === p.pub_id ? "true" : undefined}
              className={`w-full text-left px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-200 hover:bg-stone-100 transition-colors ${
                selected?.pub_id === p.pub_id ? "bg-stone-200 hover:bg-stone-200 border-l-4 border-l-stone-700 [&_.font-medium]:font-bold" : ""
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {p.source === "auto_prazo_audiencia" ? (
                  <>
                    <ClipboardPaste className="w-3 h-3 text-amber-700" strokeWidth={2} />
                    <span className="text-[10px] font-bold tracking-widest uppercase text-amber-700">
                      Conferir ata
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3 text-emerald-brand" strokeWidth={2} />
                    <span className="text-[10px] font-bold tracking-widest uppercase text-stone-500">
                      {Math.round((p.hearing?.confidence || 0) * 100)}% conf.
                    </span>
                  </>
                )}
              </div>
              <div className="font-medium text-sm text-stone-900 truncate">
                {p.hearing?.parties || p.hearing?.case_number || "Processo não identificado"}
              </div>
              <div className="text-xs text-stone-500 mt-1 tabular-nums">
                {p.source === "auto_prazo_audiencia" ? "Prevista para " : ""}
                {formatDate(p.hearing?.date) || "—"} · {p.hearing?.time || "—"}
              </div>
              <div className="text-xs text-stone-500 truncate">{p.hearing?.court || p.court_origin || "—"}</div>
            </button>
          ))}
        </aside>

        <section ref={detalheRef} className="lg:col-span-2 lg:overflow-y-auto bg-stone-50 scroll-mt-16">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-stone-400">
              <div className="text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
                <p>Selecione uma publicação à esquerda.</p>
              </div>
            </div>
          ) : isAtaItem ? (
            <div className="p-4 sm:p-8 pb-0 sm:pb-0 space-y-6" data-testid="ata-review-card">
              <div className="bg-white border border-stone-200 rounded-md p-6">
                {/* Lote 57: pra audiência agendada à mão, essa etiqueta de topo vira o
                    primeiro "botão" (verde, igual aos de salvar) lá embaixo, junto de
                    "Colar ata" — ver isManualAudiencia. */}
                {!isManualAudiencia && (
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardPaste className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
                    <span className="text-[11px] font-bold tracking-widest uppercase text-amber-700">
                      Conferir prazos da ata
                    </span>
                  </div>
                )}
                <h2 className="font-display text-lg font-semibold text-stone-900">
                  {selected.hearing?.parties || selected.hearing?.case_number || "Audiência sem identificação"}
                </h2>
                <p className="text-xs text-stone-500 mt-1">
                  {selected.hearing_date ? `Audiência em ${formatDate(selected.hearing_date)}` : null}
                  {selected.hearing?.case_number ? <> · <ProcessoLink numero={selected.hearing.case_number} /></> : ""}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {selected.source_pub_id && (
                    <Link
                      to={`/audiencia/${selected.source_pub_id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-brand hover:underline"
                    >
                      Ver audiência <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                  )}
                  <button
                    type="button"
                    data-testid="ata-open-calculator"
                    onClick={() => openCalculator({
                      data_ato: selected.hearing_date,
                      case_area: selected.hearing?.case_area,
                    })}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-stone-900 hover:underline"
                  >
                    <Calculator className="w-3.5 h-3.5" strokeWidth={2} /> Calcular prazo
                  </button>
                </div>
                {ataAindaNaoChegou ? (
                  <p className="text-sm text-stone-500 mt-4 max-w-xl" data-testid="ata-ainda-nao-chegou">
                    Prevista para {formatDate(selected.hearing?.date)} — ainda não chegou. Esse lembrete
                    só vira pendência depois dessa data; se já tiver a ata em mãos, pode colar o texto
                    abaixo e conferir os prazos antes.
                  </p>
                ) : (
                  <Dica className="text-sm text-stone-500 mt-4 max-w-xl">{`Essa audiência completou alguns dias úteis — se a ciência de um prazo ficou só registrada na ata (sem intimação formal), é hora de conferir. Cole o texto da ata, a IA sugere os prazos, e você confirma ou ajusta data/horário/anotações antes de agendar.`}</Dica>
                )}

                {ataSuggestions ? (
                  <div className="mt-5 space-y-3">
                    {ataSuggestions.length === 0 ? (
                      <p className="text-sm text-stone-500">
                        Nenhum prazo sugerido — pode confirmar assim mesmo para resolver a
                        pendência, ou colar outro texto.
                      </p>
                    ) : (
                      ataSuggestions.map((s, idx) => (
                        <div key={idx} className="border border-stone-200 rounded-md p-4 space-y-3" data-testid={`ata-suggestion-${idx}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs text-stone-500">Descrição</Label>
                              <Input
                                value={s.description}
                                onChange={(e) => updateAtaSuggestion(idx, "description", e.target.value)}
                                data-testid={`ata-suggestion-desc-${idx}`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="mt-5 text-stone-400 hover:text-rose-700 shrink-0"
                              onClick={() => removeAtaSuggestion(idx)}
                              data-testid={`ata-remove-suggestion-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-stone-500">Data</Label>
                              <Input
                                type="text"
                                inputMode="numeric"
                                placeholder="dd/mm/aaaa"
                                maxLength={10}
                                value={s.date}
                                onChange={(e) => updateAtaSuggestion(idx, "date", maskDateInput(e.target.value))}
                                data-testid={`ata-suggestion-date-${idx}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-stone-500">Horário</Label>
                              <Input
                                type="text"
                                inputMode="numeric"
                                placeholder="hh:mm"
                                maxLength={5}
                                value={s.time}
                                onChange={(e) => updateAtaSuggestion(idx, "time", maskTimeInput(e.target.value))}
                                data-testid={`ata-suggestion-time-${idx}`}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-stone-500">Tipo de prazo (opcional)</Label>
                            <Input
                              value={s.deadline_type || ""}
                              onChange={(e) => updateAtaSuggestion(idx, "deadline_type", e.target.value)}
                              placeholder="Ex: Contestação, Recurso Ordinário, Réplica…"
                              data-testid={`ata-suggestion-deadline-type-${idx}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-stone-500">Anotações (opcional)</Label>
                            <Textarea
                              rows={2}
                              value={s.notes}
                              onChange={(e) => updateAtaSuggestion(idx, "notes", e.target.value)}
                              placeholder="Observações sobre este prazo…"
                              data-testid={`ata-suggestion-notes-${idx}`}
                            />
                          </div>
                        </div>
                      ))
                    )}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        data-testid="ata-confirm-suggestions"
                        onClick={ataConfirmar}
                        disabled={ataBusy}
                        className="bg-emerald-brand text-white gap-2"
                      >
                        <Check className="w-4 h-4" strokeWidth={2} />
                        {ataBusy ? "Agendando…" : `Confirmar e agendar${ataSuggestions.length ? ` (${ataSuggestions.length})` : ""}`}
                      </Button>
                      <Button variant="outline" onClick={() => setAtaSuggestions(null)} disabled={ataBusy}>
                        Voltar
                      </Button>
                    </div>
                  </div>
                ) : ataOpen ? (
                  <div className="mt-5 space-y-3">
                    <Textarea
                      data-testid="ata-text-input"
                      rows={6}
                      autoFocus
                      value={ataText}
                      onChange={(e) => setAtaText(e.target.value)}
                      placeholder="Cole aqui o texto da ata da audiência…"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid="ata-submit"
                        onClick={ataSugerir}
                        disabled={ataBusy}
                        className="bg-emerald-brand text-white gap-2"
                      >
                        <Sparkles className="w-4 h-4" strokeWidth={2} />
                        {ataBusy ? "Analisando…" : "Sugerir prazos"}
                      </Button>
                      <Button variant="outline" onClick={() => { setAtaOpen(false); setAtaText(""); }} disabled={ataBusy}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
<>
                  <div className="flex flex-wrap items-center gap-2 mt-5 max-md:fixed max-md:inset-x-0 max-md:bottom-[84px] md:sticky md:bottom-0 z-20 bg-white/95 backdrop-blur md:-mx-6 md:-mb-6 px-4 sm:px-6 py-3 border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:shadow-none" data-testid="acoes-fixas-ata">
                    {/* Lote 57: pra audiência agendada à mão, a etiqueta que normalmente
                        fica no topo do card (amber, "Conferir prazos da ata") vira esse
                        selo verde aqui, como primeiro item da linha, antes de "Colar ata" —
                        mesma cor dos botões de salvar do app. É só um rótulo (não é
                        clicável) — não substitui nenhuma das duas ações ao lado. */}
                    {isManualAudiencia && (
                      <span
                        data-testid="ata-manual-badge"
                        className="inline-flex items-center gap-1.5 bg-emerald-brand text-white text-[11px] font-bold tracking-widest uppercase px-3 py-2 rounded-md"
                      >
                        <ClipboardPaste className="w-3.5 h-3.5" strokeWidth={2} /> Conferir prazos da ata/intimação
                      </span>
                    )}
                    <Button
                      data-testid="ata-open"
                      onClick={() => { setAtaOpen(true); setAtaText(""); }}
                      className="bg-emerald-brand text-white gap-2"
                    >
                      <ClipboardPaste className="w-3.5 h-3.5" strokeWidth={2} /> Colar ata
                    </Button>
                    <Button
                      data-testid="ata-dismiss"
                      variant="outline"
                      onClick={ataDispensar}
                      disabled={ataBusy}
                      className="gap-2 text-stone-500"
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={2} /> Validar
                    </Button>
                  </div>
                    <div className="md:hidden h-[110px]" aria-hidden="true" />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-8 pb-0 sm:pb-0 space-y-6">
              <div className="bg-white border border-stone-200 rounded-md p-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                  <span className="text-[11px] font-bold tracking-widest uppercase text-stone-500">
                    Texto original do DJEN
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-stone-700 whitespace-pre-wrap">
                  {brDatesInText(selected.raw_text)}
                </p>
                {selected.court_origin && (
                  <p className="text-xs text-stone-500 mt-3 pt-3 border-t border-stone-200">
                    Órgão: <strong>{selected.court_origin}</strong> · Publicado: {formatDate(selected.published_at) || "—"}
                  </p>
                )}
              </div>

              <div className="bg-white border border-stone-200 rounded-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-emerald-brand" strokeWidth={2} />
                  <span className="text-[11px] font-bold tracking-widest uppercase text-emerald-brand">
                    Extraído por IA — evento já criado. Corrija se necessário e valide.
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">Data</Label>
                    <Input
                      data-testid="edit-date"
                      type="text"
                      inputMode="numeric"
                      placeholder="dd/mm/aaaa"
                      maxLength={10}
                      value={dateText}
                      onChange={(e) => setDateText(maskDateInput(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">Horário</Label>
                    <Input
                      data-testid="edit-time"
                      type="text"
                      inputMode="numeric"
                      placeholder="hh:mm"
                      maxLength={5}
                      value={timeText}
                      onChange={(e) => setTimeText(maskTimeInput(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">Vara</Label>
                    <Input
                      data-testid="edit-court"
                      value={selected.hearing.court || ""}
                      onChange={(e) => updateHearing("court", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">Comarca</Label>
                    <Input
                      data-testid="edit-court-district"
                      value={selected.hearing.court_district || ""}
                      onChange={(e) => updateHearing("court_district", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-stone-500 mb-1.5 block">Número do processo</Label>
                    <Input
                      data-testid="edit-case"
                      value={selected.hearing.case_number || ""}
                      onChange={(e) => updateHearing("case_number", e.target.value)}
                    />
                  </div>
                  {selected.hearing.event_type === "prazo" && (
                    <div className="col-span-2">
                      <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de prazo</Label>
                      <Input
                        data-testid="edit-deadline-type"
                        value={selected.hearing.deadline_type || ""}
                        onChange={(e) => updateHearing("deadline_type", e.target.value)}
                        placeholder="Ex: Contestação, Recurso Ordinário, Réplica…"
                      />
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de ação</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {CASE_AREAS.map(({ value, label }) => (
                        <button
                          key={value}
                          data-testid={`edit-case-area-${value}`}
                          type="button"
                          onClick={() => updateHearing("case_area", value)}
                          className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                            (selected.hearing.case_area || "outro") === value
                              ? "bg-stone-900 text-white border-stone-900"
                              : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">
                      {PARTY_LABELS[selected.hearing.case_area || "outro"][0]}
                    </Label>
                    <Input
                      data-testid="edit-party-author"
                      value={selected.hearing.party_author || ""}
                      onChange={(e) => updateHearing("party_author", e.target.value)}
                      placeholder="A IA tenta preencher sozinha"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">
                      {PARTY_LABELS[selected.hearing.case_area || "outro"][1]}
                    </Label>
                    <Input
                      data-testid="edit-party-defendant"
                      value={selected.hearing.party_defendant || ""}
                      onChange={(e) => updateHearing("party_defendant", e.target.value)}
                      placeholder="A IA tenta preencher sozinha"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-stone-500 mb-1.5 block">Vincular ao cliente</Label>
                    <Select
                      value={selected.client_id || "none"}
                      onValueChange={(v) => setSelected({ ...selected, client_id: v === "none" ? null : v })}
                    >
                      <SelectTrigger data-testid="edit-client-select">
                        <SelectValue placeholder="Selecione um cliente…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sem cliente —</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2 sm:gap-3 max-md:flex-row-reverse max-md:fixed max-md:inset-x-0 max-md:bottom-[84px] md:flex-wrap md:sticky md:bottom-0 z-20 bg-white/95 backdrop-blur md:-mx-6 md:-mb-6 px-3 sm:px-6 py-3 border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:shadow-none [&>button]:min-h-[44px] max-md:[&>button]:px-3 max-md:[&>button:not(:first-child)_svg]:hidden" data-testid="acoes-fixas">
                  {/* Lote 26: mesmo ajuste do formulário de evento manual — o botão não
                      trava mais aparência/clique. approve() valida de verdade e mostra
                      aviso claro se faltar algo. Desde o Lote 29, data/hora são campos de
                      texto com máscara (dateText/timeText), não mais o input nativo. */}
                  <Button
                    data-testid="approve-button"
                    onClick={approve}
                    disabled={loading}
                    className="bg-emerald-brand text-white flex-1"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={2} />
                    Validar<span className="max-md:hidden">&nbsp;dados</span>
                  </Button>
                  <Button
                    data-testid="save-button"
                    variant="outline"
                    onClick={saveEdit}
                    disabled={loading}
                  >
                    Salvar<span className="max-md:hidden">&nbsp;edição</span>
                  </Button>
                  <Button
                    data-testid="reject-button"
                    variant="outline"
                    onClick={reject}
                    disabled={loading}
                    className="border-rose-200 text-rose-brand hover:bg-rose-100 hover:text-rose-700"
                  >
                    <XCircle className="w-4 h-4 mr-2" strokeWidth={2} />
                    Descartar
                  </Button>
                </div>
                <div className="md:hidden h-[84px]" aria-hidden="true" />
              </div>
            </div>
          )}
        </section>
      </div>

      <PrazoCalculator open={calcOpen} onOpenChange={setCalcOpen} initial={calcPrefill} />
    </div>
  );
}

function ManualPubDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const r = await http.post("/publications/manual", { raw_text: text });
      if (r.data.mentions_hearing) {
        toast.success("Audiência agendada", { description: "Já está no Google Calendar. Valide se quiser conferir." });
      } else {
        toast.info("Publicação adicionada, mas a IA não identificou audiência");
      }
      setText("");
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-manual-pub-button" variant="outline" className="gap-2">
          <Plus className="w-4 h-4" strokeWidth={2} /> Colar publicação
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Colar publicação manualmente</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-stone-500">Texto da publicação do diário</Label>
          <Textarea
            data-testid="manual-pub-textarea"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Cole aqui o texto integral da publicação..."
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            data-testid="submit-manual-pub"
            onClick={submit}
            disabled={loading || !text.trim()}
            className="bg-stone-900 hover:bg-stone-800 text-white"
          >
            {loading ? "Analisando com IA…" : "Analisar e enfileirar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
